import { KeyboardEvent } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '@/atomic/atoms';
import { Country, ZoneTypeName } from '../growCategoryRestriction.types';
import { GCR, gcrCls } from '../growCategoryRestriction.classes';

const ZONE_OPTIONS = Object.entries(ZoneTypeName).map(([key, label]) => ({ key, label }));

const CONTROL_HEIGHT = 38;

const labelSx = {
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: 'text.secondary',
    mb: '4px',
};

const sharedSelectSx = {
    height: CONTROL_HEIGHT,
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontSize: 13,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0,0,0,0.15)',
        transition: 'border-color 0.2s',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(25,118,210,0.5)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2', borderWidth: 1.5 },
};

interface FilterBarProps {
    countries: Country[];
    selectedCountryId: number | '';
    onCountryChange: (countryId: number) => void;
    typeName: ZoneTypeName | '';
    onTypeNameChange: (value: ZoneTypeName | '') => void;
    searchText: string;
    onSearchTextChange: (value: string) => void;
    onClear: () => void;
    onFilter: () => void;
    onRestrict: () => void;
    isFiltering?: boolean;
    isRestricting?: boolean;
    /** False when there are no pending restriction changes to save. */
    hasRestrictionChanges?: boolean;
}

export const FilterBar = ({
    countries,
    selectedCountryId,
    onCountryChange,
    typeName,
    onTypeNameChange,
    searchText,
    onSearchTextChange,
    onClear,
    onFilter,
    onRestrict,
    isFiltering = false,
    isRestricting = false,
    hasRestrictionChanges = false,
}: FilterBarProps) => {
    const { t } = useTranslation();

    const handleCountryChange = (e: SelectChangeEvent<number | ''>) => onCountryChange(e.target.value as number);

    const handleTypeNameChange = (e: SelectChangeEvent<ZoneTypeName | ''>) =>
        onTypeNameChange(e.target.value as ZoneTypeName | '');

    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchText.trim().length >= 2) onFilter();
    };

    return (
        <Box
            className={GCR.filterBar}
            sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 2,
                px: 3,
                py: 2,
                background: 'linear-gradient(180deg, #ffffff 0%, #f8faff 100%)',
                borderBottom: '1px solid rgba(25,118,210,0.1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            {/* Country */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 190 }}>
                <Typography sx={labelSx}>{t('growCategoryRestriction.filter.country')}</Typography>
                <FormControl size="small" className={gcrCls(GCR.input, GCR.inputSelectCountry)}>
                    <Select
                        value={selectedCountryId}
                        onChange={handleCountryChange}
                        displayEmpty
                        disabled={isFiltering}
                        renderValue={(value) => {
                            if (typeof value !== 'number')
                                return (
                                    <span style={{ color: '#bbb', fontSize: 13 }}>
                                        {t('growCategoryRestriction.filter.selectCountry')}
                                    </span>
                                );
                            const c = countries.find((x) => x.countryId === value);
                            return c ? `${c.translatedCountryName} (${c.countryCode})` : '';
                        }}
                        sx={{ ...sharedSelectSx, minWidth: 190 }}
                    >
                        {countries.map((c) => (
                            <MenuItem key={c.countryId} value={c.countryId} sx={{ fontSize: 13 }}>
                                {c.translatedCountryName} ({c.countryCode})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Zone Filter */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 150 }}>
                <Typography sx={labelSx}>{t('growCategoryRestriction.filter.zoneFilter')}</Typography>
                <FormControl size="small" className={gcrCls(GCR.input, GCR.inputSelectZone)}>
                    <Select
                        value={typeName}
                        onChange={handleTypeNameChange}
                        displayEmpty
                        disabled={isFiltering}
                        renderValue={(value) => {
                            if (!value)
                                return (
                                    <span style={{ color: '#bbb', fontSize: 13 }}>
                                        {t('growCategoryRestriction.filter.allZones')}
                                    </span>
                                );
                            return <span style={{ fontSize: 13 }}>{value}</span>;
                        }}
                        sx={{ ...sharedSelectSx, minWidth: 150 }}
                    >
                        <MenuItem value="" sx={{ fontSize: 13 }}>
                            <em>{t('growCategoryRestriction.filter.allZones')}</em>
                        </MenuItem>
                        {ZONE_OPTIONS.map(({ key, label }) => (
                            <MenuItem key={key} value={label} sx={{ fontSize: 13 }}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Category Search */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, maxWidth: 360 }}>
                <Typography sx={labelSx}>{t('growCategoryRestriction.filter.categorySearch')}</Typography>
                <TextField
                    size="small"
                    placeholder={t('growCategoryRestriction.filter.categorySearchPlaceholder')}
                    value={searchText}
                    onChange={(e) => onSearchTextChange(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    disabled={isFiltering}
                    className={gcrCls(GCR.input, GCR.inputTextfield)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: CONTROL_HEIGHT,
                            borderRadius: '8px',
                            fontSize: 13,
                            backgroundColor: '#fff',
                            '& fieldset': { borderColor: 'rgba(0,0,0,0.15)', transition: 'border-color 0.2s' },
                            '&:hover fieldset': { borderColor: 'rgba(25,118,210,0.5)' },
                            '&.Mui-focused fieldset': { borderColor: '#1976d2', borderWidth: 1.5 },
                        },
                    }}
                />
            </Box>

            {/* Clear + Filter buttons */}
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', pb: '1px' }}>
                <Button
                    size="small"
                    onClick={onClear}
                    disabled={isFiltering}
                    startIcon={<ClearIcon sx={{ fontSize: '20px !important' }} />}
                    className={gcrCls(GCR.input, GCR.btnClear)}
                    sx={{
                        height: CONTROL_HEIGHT,
                        minWidth: '100%',
                        px: 2,
                        border: '1px solid rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        '& .MuiButton-startIcon': { mr: 0.75, ml: -0.25 },
                        transition: 'all 0.15s',
                        '&:hover': {
                            borderColor: '#ef4444',
                            color: '#ef4444',
                            backgroundColor: 'rgba(239,68,68,0.05)',
                        },
                    }}
                >
                    {t('general.clear')}
                </Button>

                <Button
                    size="small"
                    onClick={onFilter}
                    disabled={isFiltering || searchText.trim().length < 2}
                    startIcon={<FilterListIcon sx={{ fontSize: '20px !important' }} />}
                    className={gcrCls(GCR.input, GCR.btnFilter)}
                    sx={{
                        height: CONTROL_HEIGHT,
                        minWidth: '100%',
                        px: 2,
                        border: '1px solid #1976d2',
                        borderRadius: '8px',
                        color: '#1976d2',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                        '& .MuiButton-startIcon': { mr: 0.75, ml: -0.25 },
                        transition: 'all 0.15s',
                        '&:hover': {
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            boxShadow: '0 2px 8px rgba(25,118,210,0.35)',
                        },
                        '&.Mui-disabled': {
                            borderColor: 'rgba(0,0,0,0.12)',
                            color: 'rgba(0,0,0,0.26)',
                        },
                    }}
                >
                    {t('growCategoryRestriction.filter.filter')}
                </Button>
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* Kısıtla */}
            <Box sx={{ pb: '1px' }}>
                <SCUIButton
                    text={t('growCategoryRestriction.filter.restrict')}
                    type="primary"
                    sizeType="fixed"
                    size="m"
                    buttonType="button"
                    showStartIcon
                    startIcon={<LockIcon sx={{ color: '#fff', fontSize: 18 }} />}
                    onClick={onRestrict}
                    disabled={isRestricting || isFiltering || !hasRestrictionChanges}
                    className={gcrCls(GCR.input, GCR.inputButton, GCR.btnRestrict)}
                />
            </Box>
        </Box>
    );
};
